import { ChangeDetectionStrategy, Component, OnInit, inject, input, output, signal } from '@angular/core';

import { DictionaryItem, DictionaryType, ProtocolDictionariesService } from '@lib/protocols/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, debounceTime, switchMap, take } from 'rxjs';

@Component({
  selector: 'protocols-dictionary-code-picker',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './dictionary-code-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryCodePickerComponent implements OnInit {
  private readonly service = inject(ProtocolDictionariesService);

  readonly dictionary = input.required<DictionaryType>();
  readonly select = output<DictionaryItem>();

  protected readonly term = signal('');
  protected readonly results = signal<DictionaryItem[]>([]);
  protected readonly continuationToken = signal<string | null>(null);
  protected readonly loading = signal(false);
  protected readonly open = signal(false);

  private readonly search$ = new Subject<string>();

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        switchMap(term => {
          this.loading.set(true);
          return this.service.search<DictionaryItem>(this.dictionary(), { searchTerm: term });
        })
      )
      .subscribe({
        next: page => {
          this.results.set(page.items);
          this.continuationToken.set(page.continuationToken ?? null);
          this.loading.set(false);
          this.open.set(true);
        },
        error: () => this.loading.set(false),
      });
  }

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.term.set(value);
    if (value.trim().length >= 2) {
      this.search$.next(value.trim());
    } else {
      this.results.set([]);
      this.open.set(false);
    }
  }

  protected loadMore(): void {
    const token = this.continuationToken();
    if (!token || this.loading()) {
      return;
    }

    this.loading.set(true);
    this.service
      .search<DictionaryItem>(this.dictionary(), { searchTerm: this.term().trim(), continuationToken: token })
      .pipe(take(1))
      .subscribe({
        next: page => {
          this.results.set([...this.results(), ...page.items]);
          this.continuationToken.set(page.continuationToken ?? null);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected choose(item: DictionaryItem): void {
    this.select.emit(item);
    this.term.set('');
    this.results.set([]);
    this.open.set(false);
  }

  protected close(): void {
    this.open.set(false);
  }
}
